import React, { useState, useEffect, useRef } from 'react';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { authFetch } from '../../utils/authInterceptor';

/**
 * SqlexecPasswordAuthDialog - Modal de autorización por contraseña para
 * acciones sensibles (cancelar/eliminar). Reemplaza al confirm dialog normal
 * para las acciones marcadas con requiere_autorizacion:true en
 * sqlexec_acciones.menu_acciones[]. Zero-hardcode: colores desde
 * botones_ui/modales_ui/formulario_ui (sisconfig), mismo contrato visual
 * que SqlexecConfirmDialog.jsx.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onAuthorized - callback(via) cuando el backend confirma autorizado:true
 * @param {string} componentForm - component_form a autorizar (el dueño real del flag)
 * @param {string} accionLabel - texto de la acción, para el mensaje ("Cancelar CFDI", "Eliminar")
 * @param {string} mensajeConfirmacion - pregunta de confirmación específica de la acción
 *   (ej. "¿Deseas eliminar el Cliente X?"). Si se provee, reemplaza al título genérico:
 *   la contraseña ES la confirmación, un solo paso (mismo espíritu que Google/Hotmail).
 */
const SqlexecPasswordAuthDialog = ({
  isOpen = false,
  onClose,
  onAuthorized,
  componentForm,
  accionLabel = 'esta acción',
  mensajeConfirmacion = ''
}) => {
  const { modales_ui, botones_ui, formulario_ui } = useSisConfig();

  const [password, setPassword] = useState('');
  const [intentos, setIntentos] = useState(0);
  const [bloqueadoHasta, setBloqueadoHasta] = useState(null);
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [error, setError] = useState('');
  const [verificando, setVerificando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setError('');
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!bloqueadoHasta) return;
    const tick = () => {
      const restante = Math.max(0, Math.ceil((bloqueadoHasta - Date.now()) / 1000));
      setSegundosRestantes(restante);
      if (restante <= 0) {
        setBloqueadoHasta(null);
        setIntentos(0);
      }
    };
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [bloqueadoHasta]);

  if (!isOpen) return null;

  const bloqueado = segundosRestantes > 0;
  const inputStyles = formulario_ui?.inputs || {};
  const labelStyles = formulario_ui?.labels || {};
  const errorStyles = formulario_ui?.errorMessages || {};
  const btnDanger = botones_ui?.danger || {};
  const iconColor = btnDanger.backgroundColor || '#dc2626';

  const intentar = async () => {
    if (bloqueado || verificando || !password) return;
    setVerificando(true);
    setError('');
    try {
      const r = await authFetch('/api/sqlexec/autorizar-accion', {
        method: 'POST',
        body: JSON.stringify({ component_form: componentForm, password })
      });
      const j = await r.json().catch(() => ({}));

      if (r.status === 429 || j?.error?.code === 'AUTH_LOCKED') {
        setBloqueadoHasta(Date.now() + 60 * 1000);
        setError('Demasiados intentos. Espera 1 minuto antes de volver a intentar.');
        setPassword('');
        return;
      }

      if (j?.success && j?.data?.autorizado) {
        onAuthorized?.(j.data.via);
        return;
      }

      const nuevosIntentos = intentos + 1;
      setIntentos(nuevosIntentos);
      setPassword('');
      setError(
        nuevosIntentos >= 3
          ? 'Contraseña inválida. Bloqueado por 1 minuto.'
          : `Contraseña inválida. Intentos restantes: ${3 - nuevosIntentos}.`
      );
    } catch (e) {
      setError('Error de conexión al verificar la autorización.');
    } finally {
      setVerificando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      onClick={verificando ? null : onClose}
      style={{
        backgroundColor: modales_ui?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.6)',
        backdropFilter: modales_ui?.overlay?.backdropBlur ? `blur(${modales_ui.overlay.backdropBlur})` : 'blur(4px)'
      }}
    >
      <div
        className="w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: modales_ui?.container?.backgroundColor || '#ffffff',
          borderRadius: modales_ui?.container?.borderRadius || '12px',
          boxShadow: modales_ui?.container?.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: modales_ui?.container?.border || '1px solid rgba(0, 0, 0, 0.05)',
          maxWidth: '28rem',
          overflow: 'hidden'
        }}
      >
        <div
          className="text-center"
          style={{
            padding: modales_ui?.content?.padding || '2rem',
            paddingTop: modales_ui?.content?.paddingTop || '2rem',
            paddingBottom: modales_ui?.content?.paddingBottom || '1rem'
          }}
        >
          <div
            className="mx-auto flex items-center justify-center mb-4"
            style={{ width: '88px', height: '88px', backgroundColor: 'rgba(220, 38, 38, 0.12)', borderRadius: '50%' }}
          >
            <ShieldAlert size={48} style={{ color: iconColor }} />
          </div>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', fontFamily: 'inherit', marginBottom: '1rem' }}>
            {mensajeConfirmacion || 'Esta acción requiere autorización'}
          </h3>

          <label
            style={{
              display: 'block',
              textAlign: 'left',
              fontSize: labelStyles.fontSize || '0.8125rem',
              fontWeight: labelStyles.fontWeight || '500',
              color: labelStyles.textColor || '#374151',
              marginBottom: labelStyles.marginBottom || '0.375rem'
            }}
          >
            {mensajeConfirmacion
              ? 'Ingresa tu contraseña para confirmar'
              : `Contraseña de autorización para ${accionLabel}`}
          </label>
          <input
            ref={inputRef}
            type="password"
            value={password}
            disabled={bloqueado || verificando}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') intentar(); }}
            autoComplete="off"
            style={{
              width: '100%',
              fontSize: inputStyles.fontSize || '0.875rem',
              borderRadius: inputStyles.borderRadius || '6px',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: inputStyles.borderColor || '#d1d5db',
              backgroundColor: inputStyles.backgroundColor || '#ffffff',
              padding: '0.5rem 0.75rem',
              opacity: (bloqueado || verificando) ? (inputStyles.disabledOpacity || 0.6) : 1
            }}
          />

          {error && (
            <p
              style={{
                textAlign: 'left',
                fontSize: errorStyles.fontSize || '0.8125rem',
                color: errorStyles.textColor || '#dc2626',
                marginTop: errorStyles.marginTop || '0.5rem'
              }}
            >
              {error}
            </p>
          )}
        </div>

        <div
          className="flex"
          style={{
            paddingLeft: modales_ui?.footer?.paddingX || '2rem',
            paddingRight: modales_ui?.footer?.paddingX || '2rem',
            paddingTop: modales_ui?.footer?.paddingY || '1.25rem',
            paddingBottom: modales_ui?.footer?.paddingBottom || modales_ui?.footer?.paddingY || '1.25rem',
            borderTop: modales_ui?.footer?.borderTop || '1px solid #e5e7eb',
            backgroundColor: modales_ui?.footer?.backgroundColor || '#fafafa',
            gap: modales_ui?.footer?.gap || '0.75rem',
            justifyContent: 'center'
          }}
        >
          <button
            onClick={onClose}
            disabled={verificando}
            style={{
              height: botones_ui?.height || '36px',
              fontSize: botones_ui?.fontSize || '0.875rem',
              fontWeight: botones_ui?.fontWeight || '500',
              paddingLeft: '1.5rem',
              paddingRight: '1.5rem',
              borderRadius: botones_ui?.borderRadius || '6px',
              borderWidth: botones_ui?.borderWidth || '1px',
              borderStyle: 'solid',
              borderColor: botones_ui?.secondary?.borderColor || '#d1d5db',
              backgroundColor: botones_ui?.secondary?.backgroundColor || '#ffffff',
              color: botones_ui?.secondary?.textColor || '#374151',
              cursor: verificando ? 'not-allowed' : 'pointer'
            }}
          >
            Cancelar
          </button>

          <button
            onClick={intentar}
            disabled={verificando || bloqueado || !password}
            style={{
              height: botones_ui?.height || '36px',
              fontSize: botones_ui?.fontSize || '0.875rem',
              fontWeight: botones_ui?.fontWeight || '500',
              paddingLeft: '1.5rem',
              paddingRight: '1.5rem',
              borderRadius: botones_ui?.borderRadius || '6px',
              borderWidth: botones_ui?.borderWidth || '1px',
              borderStyle: 'solid',
              borderColor: (verificando || bloqueado) ? (btnDanger.disabledBorderColor || '#9ca3af') : (btnDanger.borderColor || iconColor),
              backgroundColor: (verificando || bloqueado) ? (btnDanger.disabledBackgroundColor || '#9ca3af') : (btnDanger.backgroundColor || iconColor),
              color: btnDanger.textColor || '#ffffff',
              cursor: (verificando || bloqueado || !password) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {verificando ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Verificando...</span>
              </>
            ) : bloqueado ? (
              `Reintentar en ${segundosRestantes}s`
            ) : (
              'Autorizar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SqlexecPasswordAuthDialog;
